import React from "react";
import { useTranslation } from "react-i18next";
import { Switch } from "@/components/ui/switch";
import Text from "@/components/ui/text";
import IconContainer from "@/components/ui/icon-container";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { InfoCircle, Asterisk } from "@dreamhub/designsystem/icons";

type WorkflowRequiredFieldVariant = "default" | "warning";

interface WorkflowRequiredFieldSectionProps {
  isRequiredForPass: boolean;
  onRequiredForPassChange: (required: boolean) => void;
  className?: string;
  translationPrefix: string;
  variant?: WorkflowRequiredFieldVariant;
  icon?: React.ComponentType;
  iconColor?: "primary" | "warning" | "secondary";
  backgroundColor?: string;
}

export const WorkflowRequiredFieldSection: React.FC<
  WorkflowRequiredFieldSectionProps
> = ({
  isRequiredForPass,
  onRequiredForPassChange,
  className = "mt-6",
  translationPrefix,
  icon,
  iconColor,
  backgroundColor
}) => {
  const { t } = useTranslation();

  const getVariantStyles = () => {
    return {
      backgroundColor: backgroundColor || "bg-surface-elevation3",
      icon: icon || Asterisk,
      iconColor: iconColor || ("primary" as const)
    };
  };

  const styles = getVariantStyles();
  const IconComponent = styles.icon;

  return (
    <div className={className}>
      <div className="flex flex-col w-full gap-[6px]">
        <Text variant="bodySecondary" className="ml-1">
          {t(`${translationPrefix}.title`)}
        </Text>
        <div className={`${styles.backgroundColor} rounded-xl p-4`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <IconContainer size="5" color={styles.iconColor}>
                <IconComponent />
              </IconContainer>
              <Text variant="body">{t(`${translationPrefix}.label`)}</Text>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="cursor-pointer">
                      <IconContainer size="sm" color="secondary">
                        <InfoCircle />
                      </IconContainer>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">
                      {t(`${translationPrefix}.tooltip`)}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Switch
              size="lg"
              checked={isRequiredForPass}
              onCheckedChange={onRequiredForPassChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
